/**
 * Marketplace Page
 * Browse and clone agent templates from the marketplace
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppLayout } from '../../components/layout';
import { templateApi, AgentTemplate, TemplateCategory, CloneTemplateRequest } from '../../services/api';
import TemplateCard from '../../components/templates/TemplateCard/TemplateCard';
import TemplateDetailModal from '../../components/templates/TemplateDetailModal/TemplateDetailModal';
import { Button, Input, Spinner } from '../../components/ui';
import styles from './MarketplacePage.module.css';

const MarketplacePage: React.FC = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<AgentTemplate[]>([]);
  const [featuredTemplates, setFeaturedTemplates] = useState<AgentTemplate[]>([]);
  const [categories, setCategories] = useState<TemplateCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<'popular' | 'rating' | 'newest' | 'name'>('popular');
  
  // Modal
  const [selectedTemplate, setSelectedTemplate] = useState<AgentTemplate | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [cloning, setCloning] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    loadTemplates();
  }, [searchQuery, selectedCategory, sortBy]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [templatesRes, featuredRes, categoriesRes] = await Promise.all([
        templateApi.getMarketplaceTemplates({ sortBy: 'popular' }),
        templateApi.getFeaturedTemplates(),
        templateApi.getCategories(),
      ]);

      if (templatesRes.data) {
        setTemplates(templatesRes.data);
      }
      if (featuredRes.data) {
        setFeaturedTemplates(featuredRes.data);
      }
      if (categoriesRes.data) {
        setCategories(categoriesRes.data);
      }
    } catch (err) {
      setError('Failed to load marketplace data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadTemplates = async () => {
    try {
      const res = await templateApi.getMarketplaceTemplates({
        search: searchQuery || undefined,
        category: selectedCategory || undefined,
        sortBy,
      });
      if (res.data) {
        setTemplates(res.data);
      }
    } catch (err) {
      console.error('Failed to load templates:', err);
    }
  };

  const handleTemplateClick = (template: AgentTemplate) => {
    setSelectedTemplate(template);
    setIsModalOpen(true);
  };

  const handleCloneTemplate = async (template: AgentTemplate, customizations?: CloneTemplateRequest) => {
    try {
      setCloning(true);
      const res = await templateApi.cloneTemplate(template.id, customizations);
      if (res.data?.assistant) {
        setIsModalOpen(false);
        // Navigate to the new assistant
        navigate(`/assistants/${res.data.assistant.id}`);
      }
    } catch (err) {
      console.error('Failed to clone template:', err);
      setError('Failed to clone template');
    } finally {
      setCloning(false);
    }
  };

  const getCategoryName = (categoryId: string): string => {
    const categoryNames: Record<string, string> = {
      'sdlc': 'Software Development',
      'productivity': 'Productivity',
      'development': 'Development',
      'testing': 'Testing & QA',
      'devops': 'DevOps & SRE',
      'security': 'Security',
      'data': 'Data & Analytics',
      'custom': 'Custom',
    };
    return categoryNames[categoryId] || categoryId;
  };

  if (loading) {
    return (
      <AppLayout>
        <div className={styles.container}>
          <div className={styles.loading}>
            <Spinner size="md" label="Loading marketplace..." />
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <h1 className={styles.title}>Agent Marketplace</h1>
          <p className={styles.subtitle}>
            Discover and deploy pre-built AI agents for your team. Clone templates and customize them to your needs.
          </p>
        </div>
        <Button 
          variant="outline" 
          onClick={() => navigate('/my-templates')}
        >
          My Templates
        </Button>
      </div>

      {/* Search and Filters */}
      <div className={styles.filters}>
        <div className={styles.searchBox}>
          <Input
            placeholder="Search templates..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={styles.searchInput}
          />
        </div>
        
        <div className={styles.filterGroup}>
          <select
            value={selectedCategory || ''}
            onChange={(e) => setSelectedCategory(e.target.value || null)}
            className={styles.select}
          >
            <option value="">All Categories</option>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.name} ({cat.count})
              </option>
            ))}
          </select>

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as any)}
            className={styles.select}
          >
            <option value="popular">Most Popular</option>
            <option value="rating">Highest Rated</option>
            <option value="newest">Newest</option>
            <option value="name">Name (A-Z)</option>
          </select>
        </div>
      </div>

      {/* Featured Section */}
      {featuredTemplates.length > 0 && !searchQuery && !selectedCategory && (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>⭐ Featured Templates</h2>
          <div className={styles.featuredGrid}>
            {featuredTemplates.map((template) => (
              <TemplateCard
                key={template.id}
                template={template}
                onClick={() => handleTemplateClick(template)}
                featured
              />
            ))}
          </div>
        </section>
      )}

      {/* All Templates */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>
          {selectedCategory ? getCategoryName(selectedCategory) : 'All Templates'}
          <span className={styles.count}>({templates.length})</span>
        </h2>
        
        {templates.length === 0 ? (
          <div className={styles.emptyState}>
            <p>No templates found matching your criteria.</p>
            <Button 
              variant="outline" 
              onClick={() => {
                setSearchQuery('');
                setSelectedCategory(null);
              }}
            >
              Clear Filters
            </Button>
          </div>
        ) : (
          <div className={styles.templatesGrid}>
            {templates.map((template) => (
              <TemplateCard
                key={template.id}
                template={template}
                onClick={() => handleTemplateClick(template)}
              />
            ))}
          </div>
        )}
      </section>

      {/* Category Browse */}
      {!searchQuery && !selectedCategory && (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Browse by Category</h2>
          <div className={styles.categoriesGrid}>
            {categories.map((category) => (
              <button
                key={category.id}
                className={styles.categoryCard}
                onClick={() => setSelectedCategory(category.id)}
              >
                <span className={styles.categoryIcon}>
                  {getCategoryIcon(category.id)}
                </span>
                <span className={styles.categoryName}>{category.name}</span>
                <span className={styles.categoryCount}>{category.count} templates</span>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Template Detail Modal */}
      {selectedTemplate && (
        <TemplateDetailModal
          template={selectedTemplate}
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onClone={handleCloneTemplate}
          cloning={cloning}
        />
      )}

      {/* Error Toast */}
      {error && (
        <div className={styles.errorToast}>
          {error}
          <button onClick={() => setError(null)}>✕</button>
        </div>
      )}
      </div>
    </AppLayout>
  );
};

function getCategoryIcon(categoryId: string): string {
  const icons: Record<string, string> = {
    'sdlc': '🔄',
    'productivity': '⚡',
    'development': '💻',
    'testing': '🧪',
    'devops': '🔧',
    'security': '🔒',
    'data': '📊',
    'custom': '✨',
  };
  return icons[categoryId] || '📦';
}

export default MarketplacePage;
